import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Trash2, BookOpen, Clock, ArrowRight, Loader2, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { getGardePages, deleteGardePage, getBooks } from "../lib/supabase";

const GardePagesPage = () => {
  const [gardePages, setGardePages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [books, setBooks] = useState({});
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();
  
  // User ID - strictly use Supabase auth UUID (no fallback)
  const userId = user?.id || null;

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch books for display names
        const booksData = await getBooks();
        const bookMap = {};
        [...booksData.ancien_testament, ...booksData.nouveau_testament].forEach(b => {
          bookMap[b.id] = b.name;
        });
        setBooks(bookMap);

        // Fetch garde-pages only if premium
        if (isPremium && userId) {
          const data = await getGardePages(userId);
          setGardePages(data || []);
        }
      } catch (error) {
        console.error("Error loading garde-pages:", error);
        toast.error("Erreur lors du chargement des garde-pages");
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [userId, isPremium]);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await deleteGardePage(deleteId);
      setGardePages(prev => prev.filter(gp => gp.id !== deleteId));
      toast.success("Garde-page supprimé");
    } catch (error) {
      console.error("Error deleting garde-page:", error);
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const navigateToPage = (gp) => {
    navigate(`/bible/${gp.book_id}/${gp.chapter}`, {
      state: { scrollToVerse: gp.verse, scrollPosition: gp.scroll_position }
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-rubric-red mb-4" />
        <p className="text-ink-secondary">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-2xl">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Bookmark className="h-7 w-7 text-rubric-red" />
          <h1 className="font-heading text-2xl sm:text-3xl text-ink-primary">
            Mes Garde-pages
          </h1>
          {!isPremium && (
            <Badge variant="outline" className="ml-2 text-gold-leaf border-gold-leaf">
              <Crown className="h-3 w-3 mr-1" />Premium
            </Badge>
          )}
        </div>
        <p className="text-sm text-ink-secondary">
          Retrouvez vos positions de lecture sauvegardées
        </p>
      </div>

      {/* Premium Gate */}
      {!isPremium ? (
        <div className="text-center py-12 bg-paper-dark border border-border rounded-lg">
          <Crown className="h-16 w-16 text-gold-leaf mx-auto mb-4" />
          <h2 className="font-heading text-xl text-ink-primary mb-2">Fonctionnalité Premium</h2>
          <p className="text-ink-secondary mb-6 px-4">
            Les garde-pages sont réservés aux membres Premium. 
            Sauvegardez vos positions de lecture et reprenez facilement où vous en étiez.
          </p>
          <Button 
            onClick={() => navigate("/premium")}
            className="bg-gold-leaf hover:bg-gold-leaf/90 text-ink-primary"
          >
            <Crown className="h-4 w-4 mr-2" />
            Passer Premium
          </Button>
        </div>
      ) : (
        <>
          {/* Info box */}
          <div className="bg-paper-dark border border-border rounded-lg p-4 mb-6">
            <p className="text-sm text-ink-secondary">
              <strong className="text-ink-primary">💡 Astuce :</strong> Pour créer un garde-page, 
              appuyez sur le bouton <Bookmark className="inline h-4 w-4 text-rubric-red mx-1" /> 
              en haut de la page de lecture.
            </p>
          </div>

          {/* Garde-pages list */}
          {gardePages.length === 0 ? (
            <div className="text-center py-12">
              <Bookmark className="h-16 w-16 text-ink-secondary/20 mx-auto mb-4" />
              <p className="text-ink-secondary mb-4">
                Vous n'avez pas encore de garde-pages
              </p>
              <Button 
                variant="outline"
                onClick={() => navigate("/bible/genese/1")}
                className="border-rubric-red text-rubric-red hover:bg-rubric-red hover:text-white"
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Commencer à lire
              </Button>
            </div>
          ) : (
            <ScrollArea className="h-[calc(100vh-350px)]">
              <div className="space-y-3 pr-4">
                {gardePages.map((gp) => (
                  <Card 
                    key={gp.id}
                    className="border-border bg-paper-dark hover:shadow-md transition-shadow"
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div 
                          className="flex-1 min-w-0 cursor-pointer"
                          onClick={() => navigateToPage(gp)}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Bookmark className="h-4 w-4 text-rubric-red flex-shrink-0" />
                            <span className="font-heading text-base text-ink-primary truncate">
                              {gp.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-ink-secondary mb-2">
                            <BookOpen className="h-3.5 w-3.5" />
                            <span>
                              {books[gp.book_id] || gp.book_id} · Chapitre {gp.chapter}
                              {gp.verse && `, verset ${gp.verse}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-ink-secondary/70">
                            <Clock className="h-3 w-3" />
                            <span>{formatDate(gp.created_at)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-ink-secondary hover:text-rubric-red hover:bg-rubric-red/10"
                            onClick={() => navigateToPage(gp)}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-9 w-9 text-ink-secondary hover:text-red-500 hover:bg-red-500/10"
                            onClick={() => setDeleteId(gp.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-paper">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce garde-page ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le garde-page sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default GardePagesPage;
